import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Segmented } from "@dl/web-core-js/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NexpimView } from "../../views";
import { ClientPricingRuleGrid } from "./ClientPricingRulePage";
import { ProductPricingRuleGrid } from "./ProductPricingRulePage";

type Scope = "product" | "client";

/**
 * Pricing rules, whether they hang off a product or a client.
 *
 * Two screens for one concept, differing only in what the rule is attached to.
 * Their rows have nothing in common though: a product rule carries a category,
 * a weekly average and a ranking, a client rule carries a client number and a
 * currency. So the switch swaps the whole grid rather than merging the rows,
 * the same shape the manual invoices page took.
 *
 * Both scopes are one permission, NEXPIM_PRICING_RULES.
 */
export function PricingRulesPage() {
  const { t } = useTranslation();
  const { hasView } = useAuth();
  const [scope, setScope] = useState<Scope>("product");

  if (!hasView(NexpimView.PRICING_RULES)) {
    return (
      <>
        <PageHeader title={t("navigation.item.pricing-rules", "Pricing rules")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const options = [
    { value: "product" as const, label: t("pricing.rules.product", "By product") },
    { value: "client" as const, label: t("pricing.rules.client", "By client") },
  ];

  const toolbar = <Segmented value={scope} onChange={setScope} options={options} />;

  return (
    <>
      <PageHeader
        title={t("navigation.item.pricing-rules", "Pricing rules")}
        subtitle={
          scope === "product"
            ? t("pricing.rules.product.subtitle", "Rules attached to a product.")
            : t("pricing.rules.client.subtitle", "Rules attached to a client.")
        }
      />
      {scope === "product" && <ProductPricingRuleGrid toolbar={toolbar} />}
      {scope === "client" && <ClientPricingRuleGrid toolbar={toolbar} />}
    </>
  );
}
